import { useCallback, useState } from 'react';
import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { environmentAtom } from '@data/fms/environment/states';
import { projectAtom } from '@data/auth';
import type { AxiosResponse, AxiosError } from 'axios';
import { useNotification } from '@data/notification';
import { useTranslation } from 'react-i18next';
import { useAtomCallback } from 'jotai/utils';

type ReuqestBody =
  | {
      path: '/name';
      value: string;
    }
  | {
      path: '/description';
      value: string;
    };

export type NoEntryZoneSettingPatchParams = {
  settingId: string;
  bodies: ReuqestBody[];
};

/**
 * 指定の通行禁止区域設定更新API
 */
export const usePatchNoEntryZoneSettingAPI = (): {
  state: APIState;
  patchNoEntryZoneSetting: (
    params: NoEntryZoneSettingPatchParams,
  ) => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      params: NoEntryZoneSettingPatchParams,
    ): Promise<Result<true, AxiosResponse>> => {
      try {
        const { bodies } = params;
        const requestBodies = bodies.map((body) => ({
          op: 'replace',
          path: body.path,
          value: body.value,
        }));
        await fmsAPI.patch(
          `/${projectId}/environments/${environmentId}/no_entry_zone_settings/${params.settingId}`,
          requestBodies,
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  /**
   * タグ更新
   */
  const patchNoEntryZoneSetting = useAtomCallback(
    useCallback(
      async (get, _, params: NoEntryZoneSettingPatchParams) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t('api.fms.no_entry_zone_setting.update', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await request(
          project.id,
          environment.environment_id,
          params,
        );
        if (!res.value || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.no_entry_zone_setting.update', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return false;
        }
        notifySuccess({
          message: t('api.fms.no_entry_zone_setting.update', {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, request, t, getErrorMessage],
    ),
  );

  return {
    state,
    patchNoEntryZoneSetting,
  };
};
